import { createFileRoute, Link } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { LegalPageLayout } from "@/components/LegalPageLayout";
import { LEGAL_LAST_UPDATED, LEGAL_LAST_UPDATED_AR } from "@/lib/legal";

export const Route = createFileRoute("/payment-protection-policy")({
  head: () => ({
    meta: [
      { title: "Payment Protection Policy — Lumvio" },
      {
        name: "description",
        content:
          "How Lumvio payment protection works: funding, review windows, auto-release, disputes and refunds on protected milestone payments.",
      },
      { property: "og:title", content: "Payment Protection Policy — Lumvio" },
      {
        property: "og:description",
        content: "How Lumvio payment protection is designed to reduce risk for freelancers and clients.",
      },
      { property: "og:type", content: "article" },
      { property: "og:url", content: "https://lumvio.co/payment-protection-policy" },
      { name: "twitter:card", content: "summary" },
      { name: "twitter:title", content: "Payment Protection Policy — Lumvio" },
      {
        name: "twitter:description",
        content: "How Lumvio payment protection is designed to reduce risk for freelancers and clients.",
      },
    ],
    links: [{ rel: "canonical", href: "https://lumvio.co/payment-protection-policy" }],
  }),
  component: PaymentProtectionPolicyPage,
});

function PaymentProtectionPolicyPage() {
  const { i18n } = useTranslation();
  const ar = (i18n.language || "en").startsWith("ar");
  return (
    <LegalPageLayout
      title={ar ? "سياسة حماية المدفوعات" : "Payment Protection Policy"}
      updated={ar ? LEGAL_LAST_UPDATED_AR : LEGAL_LAST_UPDATED}
    >
      {ar ? <ArabicBody /> : <EnglishBody />}
    </LegalPageLayout>
  );
}

function EnglishBody() {
  return (
    <>
      <p>
        Lumvio holds client funds under payment protection so both sides of a project have a clear, recorded process.
        This page explains how funding, release, and disputes work. No system can eliminate all risk or fraud; payment
        protection is designed to reduce it.
      </p>

      <h2>1. Funding</h2>
      <p>
        The client funds the project (or milestone) before work begins. Funds are held by Lumvio's payment processor
        and cannot be withdrawn by either party until the release conditions are met.
      </p>

      <h2>2. Delivery &amp; review window</h2>
      <ul>
        <li>When the freelancer marks the project complete, a 48-hour client review window begins.</li>
        <li>The client can approve the delivery to release funds immediately.</li>
        <li>The client can request an extension of up to 24 additional hours (72h total maximum).</li>
        <li>If no action is taken before the window ends, funds are auto-released to the freelancer.</li>
      </ul>

      <h2>3. Disputes</h2>
      <p>
        Either party can open a dispute during the review window. Protected funds are frozen until Lumvio reviews the
        evidence and issues a decision that is binding for the release of those funds — full release, full refund, or a
        split.
      </p>

      <h2>4. Fees</h2>
      <p>
        Lumvio charges a platform fee disclosed at the point of transaction. The fee is deducted from the released
        amount, not charged separately to the client. Third-party payment-processing fees may also apply.
      </p>

      <h2>5. Related policies</h2>
      <p>
        See also our <Link to="/refund">Refund Policy</Link>,{" "}
        <Link to="/payment-protection-refunds">Refund &amp; Payment Protection Policy</Link>, and{" "}
        <Link to="/terms">Terms of Service</Link>.
      </p>
    </>
  );
}

function ArabicBody() {
  return (
    <>
      <p>
        تحتفظ Lumvio بأموال العملاء ضمن حماية المدفوعات لتوفير مسار واضح وموثّق لطرفي المشروع. توضح هذه الصفحة آلية
        التمويل والإفراج والنزاعات. لا يمكن لأي نظام إزالة كل المخاطر أو الاحتيال؛ حماية المدفوعات مصمّمة للحد منها.
      </p>

      <h2>1. التمويل</h2>
      <p>
        يمول العميل المشروع (أو المرحلة) قبل بدء العمل. تُحتفظ الأموال لدى معالج الدفع الخاص بـ Lumvio ولا يمكن لأي طرف
        سحبها حتى تتحقق شروط الإفراج.
      </p>

      <h2>2. التسليم ونافذة المراجعة</h2>
      <ul>
        <li>عند تحديد المستقل للمشروع كمكتمل، تبدأ نافذة مراجعة للعميل مدتها 48 ساعة.</li>
        <li>يمكن للعميل الموافقة على التسليم للإفراج عن الأموال فورًا.</li>
        <li>يمكن للعميل طلب تمديد يصل إلى 24 ساعة إضافية (بحدٍّ إجمالي أقصى 72 ساعة).</li>
        <li>إذا لم يُتَّخذ أي إجراء قبل انتهاء النافذة، يتم الإفراج عن الأموال تلقائيًا إلى المستقل.</li>
      </ul>

      <h2>3. النزاعات</h2>
      <p>
        يمكن لأي طرف فتح نزاع خلال نافذة المراجعة. تُجمَّد الأموال المحمية حتى تراجع Lumvio الأدلة وتصدر قرارًا ملزمًا
        فيما يخص الإفراج عن تلك الأموال — إفراج كامل، أو استرداد كامل، أو تقسيم.
      </p>

      <h2>4. الرسوم</h2>
      <p>
        تفرض Lumvio رسم منصة يُفصح عنه عند إتمام المعاملة. يُخصم الرسم من المبلغ المُفرَج عنه، ولا يُفرض على العميل
        بشكل منفصل. وقد تُطبَّق أيضًا رسوم معالجة دفع من أطراف خارجية.
      </p>

      <h2>5. السياسات ذات الصلة</h2>
      <p>
        راجع أيضًا <Link to="/refund">سياسة الاسترداد</Link>،{" "}
        <Link to="/payment-protection-refunds">سياسة الاسترداد وحماية المدفوعات</Link>، و{" "}
        <Link to="/terms">شروط الخدمة</Link>.
      </p>
    </>
  );
}
